import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAgreementsStore } from "../store/agreements-store";
import { useAgreementConfirmation } from "../hooks/use-agreement-confirmation";
import { AlertCircle, FileText } from "lucide-react";

export const AgreementModal: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isAgreementModalOpen } = useAgreementsStore();
  const {
    agreementData,
    currentAgreement,
    isLoading,
    isPending,
    currentIndex,
    totalCount,
    handleConfirm,
    handleDecline,
  } = useAgreementConfirmation();

  // Get current language code
  const currentLanguage = i18n.resolvedLanguage || "en";

  // Get localized agreement title and content
  const agreementTitle =
    agreementData?.data?.title?.[currentLanguage] ||
    agreementData?.data?.title?.en ||
    currentAgreement?.name ||
    "";
  const agreementContent =
    agreementData?.data?.content?.[currentLanguage] ||
    agreementData?.data?.content?.en || "";

  // Handle confirm button click
  const onConfirm = () => {
    if (isLoading || isPending || !agreementContent.trim()) return;
    handleConfirm();
  };

  // Prevent modal close (user must confirm or decline)
  const handleOpenChange = () => {
    // Modal cannot be closed
    return;
  };

  // ✅ Dialog her zaman render edilmeli, open prop'u yeterli
  // if (!isAgreementModalOpen || !currentAgreement) return null; // ❌ Bu satır sorun!

  return (
    <Dialog
      open={isAgreementModalOpen && !!currentAgreement}
      onOpenChange={handleOpenChange}
    >
      <DialogContent
        open={isAgreementModalOpen && !!currentAgreement}
        className="max-w-2xl max-h-[90vh] flex flex-col bg-white dark:bg-gray-800 p-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-state={
          isAgreementModalOpen && currentAgreement ? "open" : "closed"
        }
      >
        <div className="relative z-[100] focus:outline-none w-full h-full flex flex-col overflow-hidden p-6">
          {/* Header */}
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                <DialogTitle className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {t("agreements.title")}
                </DialogTitle>
              </div>
              {totalCount > 1 && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {t("agreements.progress", {
                    current: currentIndex + 1,
                    total: totalCount,
                  })}
                </span>
              )}
            </div>
            <DialogDescription className="text-text-secondary dark:text-gray-400">
              {t("agreements.agreementRequired")}
            </DialogDescription>
          </DialogHeader>

          {/* Content */}
          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-4">
            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center space-y-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t("agreements.loading")}
                  </p>
                </div>
              </div>
            )}

            {/* Agreement Title */}
            {!isLoading && agreementTitle && (
              <div className="pb-2 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {agreementTitle}
                </h3>
              </div>
            )}

            {/* Agreement Content */}
            {!isLoading && agreementContent && (
              <div className="prose dark:prose-invert max-w-none">
                <div
                  className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: agreementContent }}
                />
              </div>
            )}

            {/* Warning Message */}
            {!isLoading && !agreementContent.trim() && (
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {t("agreements.noContent")}
              </p>
            )}
            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 dark:text-amber-300">
                {t("agreements.declineWarning")}
              </p>
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className="flex-col sm:flex-row gap-3">
            {/* Buttons */}
            <div className="flex gap-2 w-full">
              <button
                onClick={handleDecline}
                disabled={isPending}
                className="flex-1 px-3.5 py-2.5 rounded-lg bg-white dark:bg-gray-700 border border-border-primary dark:border-border-primary text-text-secondary dark:text-text-secondary text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("agreements.decline")}
              </button>
              <Button
                variant="darkPrimary"
                onClick={onConfirm}
                disabled={isLoading || isPending || !agreementContent.trim()}
                className="flex-1 text-white hover:bg-primary/90 dark:hover:bg-primary/90"
              >
                {isPending ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {t("common.loading")}
                  </span>
                ) : (
                  t("agreements.confirm")
                )}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
